import React, { useCallback, useEffect, useState } from "react";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Plus, Download } from "lucide-react";
import { SuperuserOnly } from "./SecurityMonitorPage";
import { fmtDT } from "../components/security/ActivityTab";
import { IncidentCreateDialog, SEVERITY_LABEL } from "../components/incidents/IncidentCreateDialog";
import { IncidentDetailDialog } from "../components/incidents/IncidentDetailDialog";
import { BreachClock } from "../components/incidents/BreachClock";

const SEV_STYLE = { P1: "bg-red-600 text-white", P2: "bg-red-100 text-red-700", P3: "bg-amber-100 text-amber-700", P4: "bg-slate-100 text-slate-600" };
const err = (e, m) => toast.error(e.response?.data?.detail || m);

export default function IncidentsPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "superuser";
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(() => apiClient.get("/incidents").then((r) => setRows(r.data)).catch((e) => err(e, "Could not load incidents")), []);
  useEffect(() => { if (isSuper) load(); }, [isSuper, load]);

  const open = async (id) => setActive((await apiClient.get(`/incidents/${id}`)).data);
  const create = async (body) => {
    try { const r = await apiClient.post("/incidents", body); toast.success(`${r.data.ref} logged`); setCreating(false); await load(); return true; }
    catch (e) { err(e, "Could not log incident"); return false; }
  };
  const save = async (changes) => {
    try { const r = await apiClient.put(`/incidents/${active.id}`, changes); setActive(r.data); toast.success("Incident updated"); load(); }
    catch (e) { err(e, "Could not update incident"); }
  };
  const addNote = async (note) => {
    try { await apiClient.post(`/incidents/${active.id}/timeline`, { note }); await open(active.id); return true; }
    catch (e) { err(e, "Could not add note"); return false; }
  };

  if (!isSuper) return <SuperuserOnly />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="incidents-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Incident Log</h1>
          <p className="text-slate-500 mt-1">Record, manage and evidence security incidents in line with the Incident Response Policy (POL-03)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm gap-2" onClick={() => window.open(`${apiClient.defaults.baseURL}/incidents/export/csv`, "_blank")} data-testid="export-incidents-btn"><Download size={15} /> CSV</Button>
          <Button className="rounded-sm gap-2" onClick={() => setCreating(true)} data-testid="log-incident-btn"><Plus size={15} /> Log incident</Button>
        </div>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {!rows.length ? <p className="p-10 text-center text-sm text-slate-500" data-testid="incidents-empty">No incidents recorded.</p> : (
            <table className="w-full text-sm" data-testid="incidents-table">
              <thead><tr className="border-b text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
                <th className="p-3">Ref</th><th className="p-3">Title</th><th className="p-3">Severity</th><th className="p-3">Status</th><th className="p-3">Detected</th><th className="p-3">GDPR notice</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => open(r.id)} data-testid={`incident-row-${r.ref}`}>
                    <td className="p-3 font-mono text-xs">{r.ref}</td>
                    <td className="p-3 font-medium">{r.title}</td>
                    <td className="p-3"><Badge className={`text-xs rounded-sm ${SEV_STYLE[r.severity]}`}>{SEVERITY_LABEL[r.severity]}</Badge></td>
                    <td className="p-3 capitalize">{r.status}</td>
                    <td className="p-3 text-xs text-slate-500">{fmtDT(r.detected_at)}</td>
                    <td className="p-3"><BreachClock incident={r} compact /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <IncidentCreateDialog open={creating} onOpenChange={setCreating} onCreate={create} />
      <IncidentDetailDialog incident={active} onClose={() => setActive(null)} onSave={save} onNote={addNote} />
    </div>
  );
}
